import React, { useState, useRef } from "react";
import Styles from "./Sidebar.module.scss";
import { Box, Stack, Typography, Drawer, IconButton, Menu as MuiMenu, MenuItem, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ArrowIcon from "../../assets/images/Arrow-Icon.svg";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LMT_Logo from "../../assets/images/img2/LMT_Logo.svg";
import LMT_Logo2 from "../../assets/images/img2/LMT_Logo2.svg";
import Logout from "../../assets/images/Logout.svg";

const Sidebar = ({ Menu, onNavigate, title, isCollapsed, toggleSidebar, onClose }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState({});
  const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null);
  const [activeSubMenuIndex, setActiveSubMenuIndex] = useState(null);
  const [selectedSubItem, setSelectedSubItem] = useState(null);
  const clickTimerRef = useRef(null);

  const handleChevronClick = () => {
    if (clickTimerRef.current) {
      // Double click → fully close
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      if (onClose) onClose();
    } else {
      // Start timer — if no second click within 250ms, treat as single click
      clickTimerRef.current = setTimeout(() => {
        clickTimerRef.current = null;
        toggleSidebar();
      }, 250);
    }
  };

  const handleSelect = (index) => {
    setSelectedIndex(index);
  };

  const handleSubMenu = (i, event) => {
    setOpenSubMenu((prevState) => {
      const isCurrentlyOpen = prevState[i];
      return isCurrentlyOpen ? {} : { [i]: true };
    });

    if (isCollapsed) {
      setSubmenuAnchorEl(event.currentTarget);
      setActiveSubMenuIndex(i);
    }
  };

  const handleCloseSubMenu = () => {
    setSubmenuAnchorEl(null);
    setActiveSubMenuIndex(null);
  };

  const handleToggleMobileDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  const renderSidebarContent = () => (
    <div
      className={`${Styles.SidebarContainer} ${isCollapsed ? Styles.collapsed : ""}`}
    >
      <Stack spacing={2} direction="row">
        {!isCollapsed ? (
          <Box
            sx={{
              color: "black",
              width: "100%",
              fontWeight: 800,
              fontSize: !mobileOpen ? "24px" : "0px",
              textAlign: "center",
            }}
          >
            <img
              src={LMT_Logo}
              alt="LMT-Logo"
              style={{
                height: "49px",
                width: "145px",
                margin: !mobileOpen
                  ? "11px 32px 2px 10px"
                  : "5px 10px 10px 5px",
              }}
            />
          </Box>
        ) : (
          <Box>
            <img
              src={LMT_Logo2}
              alt="LMT-Logo2"
              style={{
                height: "45px",
                width: "35px",
                margin: "14px 20px",
                justifyContent: "center",
              }}
            />
          </Box>
        )}
        <Stack sx={{ padding: isCollapsed ? "10px 0px" : "12px 0" }}>
          <Tooltip
            title={isCollapsed ? "Click to expand · Double-click to close" : "Click to collapse · Double-click to close"}
            placement="right"
            arrow
          >
            {isCollapsed ? (
              <ChevronRightIcon
                onClick={handleChevronClick}
                sx={{
                  height: "28px",
                  width: "28px",
                  position: "absolute",
                  background: "linear-gradient(135deg, #008080 0%, #00B8B8 100%)",
                  color: "#ffffff",
                  display: mobileOpen ? "none" : "flex",
                  borderRadius: "50%",
                  right: "-14px",
                  top: "55px",
                  cursor: "pointer",
                  zIndex: 999,
                  boxShadow: "0 4px 12px rgba(0, 128, 128, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 6px 16px rgba(0, 128, 128, 0.6)",
                  },
                }}
              />
            ) : (
              <ChevronLeftIcon
                onClick={handleChevronClick}
                sx={{
                  height: "28px",
                  width: "28px",
                  position: "absolute",
                  background: "linear-gradient(135deg, #008080 0%, #00B8B8 100%)",
                  color: "#ffffff",
                  display: mobileOpen ? "none" : "flex",
                  borderRadius: "50%",
                  right: "-14px",
                  top: "55px",
                  cursor: "pointer",
                  zIndex: 990,
                  boxShadow: "0 4px 12px rgba(0, 128, 128, 0.4)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.1)",
                    boxShadow: "0 6px 16px rgba(0, 128, 128, 0.6)",
                  },
                }}
              />
            )}
          </Tooltip>
          <Box
            onClick={handleToggleMobileDrawer}
            sx={{
              height: "25px",
              width: "25px",
              borderRadius: "50%",
              backgroundColor: "#5EF3FB",
              position: "absolute",
              display: mobileOpen ? "none" : "none",
              alignItems: "center",
              justifyContent: "center",
              left: isCollapsed ? "5.3%" : "90%",
              transition: "left 0.4s ease",
            }}
          >
            <img
              src={ArrowIcon}
              alt="icon"
              style={{
                transform: isCollapsed ? "rotate(0deg)" : "rotate(180deg)",
                transition: "transform 0.5s ease",
              }}
            />
          </Box>
        </Stack>
      </Stack>

      <Box mt={0} className={Styles.sideBarContent}>
        {Menu.map((e, i) => {
          const isSubMenuOpen = openSubMenu[i];
          const isSelected = selectedIndex === i;
          if (Menu && e?.subMenu?.length > 0) {
            return (
              <Box
                key={i}
                sx={{ marginBottom: isSubMenuOpen ? "20px" : "0px" }}
              >
                <Tooltip
                  title={isCollapsed ? e.title : ""}
                  placement="right"
                  arrow
                  disableHoverListener={!isCollapsed}
                >
                  <Box
                    className={`${isSelected
                      ? Styles.SidebarLabelContainerSmall
                      : Styles.SidebarLabelContainer
                      }`}
                    onClick={(ev) => handleSubMenu(i, ev)}
                  >
                    <Typography className={Styles.SidebarIcon}>
                      {e.icon}
                    </Typography>
                    {!isCollapsed && (
                      <Typography className={Styles.SidebarLabel}>
                        {e.title}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
                {isSubMenuOpen && !isCollapsed &&
                  e.subMenu.map((sub, index) => (
                    <Box
                      key={index}
                      className={`${Styles.SubNavBox} ${selectedSubItem === sub.path ? Styles.SubNavBoxActive : ""}`}
                      onClick={() => {
                        setSelectedIndex(null);
                        setSelectedSubItem(sub.path);
                        onNavigate(sub);
                      }}
                    >
                      <Box
                        className={Styles.treeLine}
                        sx={{ height: index === 0 ? "20px" : "36px" }}
                      ></Box>
                      <Typography
                        className={Styles.SubNavTitle}
                        sx={{ top: index === 0 ? "11px" : "18px" }}
                      >
                        &nbsp;{sub.title}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            );
          } else {
            return (
              <Tooltip
                key={i}
                title={isCollapsed ? e.title : ""}
                placement="right"
                arrow
                disableHoverListener={!isCollapsed}
              >
                <Box
                  className={`${Styles.SidebarLabelContainer} ${isSelected ? Styles.SidebarLabelContainerSmall : ""}`}
                  onClick={() => {
                    handleSelect(i);
                    setSelectedSubItem(null);
                    onNavigate(e);
                  }}
                >
                  {isSelected ? (
                    <Typography className={Styles.SidebarIcon}>
                      {e.icon2}
                    </Typography>
                  ) : (
                    <Typography className={Styles.SidebarIcon}>
                      {e.icon}
                    </Typography>
                  )}

                  {!isCollapsed && (
                    <Typography
                      className={`${isSelected
                        ? Styles.SidebarSelectedLabel
                        : Styles.SidebarLabel
                        }`}
                    >
                      {e.title}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          }
        })}
      </Box>

      <Tooltip
        title={isCollapsed ? "Log out" : ""}
        placement="right"
        arrow
        disableHoverListener={!isCollapsed}
      >
        <Box
          className={Styles.SidebarLogoutContainer}
          onClick={() => {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            localStorage.removeItem("userName");
            onNavigate("login");
          }}
        >
          <Typography className={Styles.SidebarIcon}>
            <LogoutOutlinedIcon />
          </Typography>
          {!isCollapsed && (
            <Typography className={Styles.SidebarLabel}>Log out</Typography>
          )}
        </Box>
      </Tooltip>

      <MuiMenu
        anchorEl={submenuAnchorEl}
        open={Boolean(submenuAnchorEl) && isCollapsed}
        onClose={handleCloseSubMenu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ ml: 2, zIndex: 10000 }}
      >
        {activeSubMenuIndex !== null &&
          Menu[activeSubMenuIndex]?.subMenu.map((sub, index) => (
            <MenuItem
              key={index}
              onClick={() => {
                setSelectedIndex(null);
                setSelectedSubItem(sub.path);
                onNavigate(sub);
                handleCloseSubMenu();
              }}
              sx={{
                minWidth: "150px",
                backgroundColor: selectedSubItem === sub.path ? "rgba(0, 128, 128, 0.08)" : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(0, 128, 128, 0.12)"
                }
              }}
            >
              <Typography variant="body2">{sub.title}</Typography>
            </MenuItem>
          ))}
      </MuiMenu>
    </div>
  );

  return (
    <>
      <IconButton
        onClick={handleToggleMobileDrawer}
        sx={{
          display: { xs: "block", sm: "none" },
          position: "fixed",
          top: "7px",
          left: "2px",
          zIndex: 1200,
        }}
      >
        <MenuIcon sx={{ color: "#3D3D3D", fontSize: "20px" }} />
      </IconButton>

      {/* Desktop Sidebar */}
      <Box
        sx={{
          display: { xs: "none", sm: "block" },
        }}
      >
        {renderSidebarContent()}
      </Box>

      {/* Mobile Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleToggleMobileDrawer}
        sx={{
          display: { xs: "block", sm: "none" },
        }}
      >
        <Box sx={{ width: 240, height: "100vh" }}>{renderSidebarContent()}</Box>
      </Drawer>
    </>
  );
};

export { Sidebar };
